import {
    ArchiveTrackedEventTypeCommand,
    CanDeletePhysicalTargetTypeResponse,
    CanDeleteTaskTypeResponse,
    CreatePhysicalTargetTypeCommand,
    CreateTaskTypeCommand,
    CreateTrackedCountTypeCommand, CreateTrackedDaysSinceLastIncidentTypeCommand,
    CreateTrackedEventTypeCommand, DepartmentDailyReportConfiguration, DepartmentDailyReportConfigurationViewModel,
    ExportResponse,
    GetKeyPhysicalTargetsForDepartmentWeekQueryResult,
    GetWeekHasExistingKeyPhysicalTargetsQueryResponse,
    KeyPhysicalTargetsImportViewModel,
    KeyPhysicalType,
    PhysicalTargetTypeViewModel,
    ReportingMonthsImportViewModel,
    ReportingMonthYearViewModel,
    RestoreTrackedEventTypeFromArchiveCommand,
    TaskType, TrackedCountTypeViewModel, TrackedDaysSinceLastIncidentTypeViewModel,
    TrackedEventTypeViewModel, UpdateDepartmentDailyReportConfigurationCommand,
    UpdatePhysicalTargetTypeCommand,
    UpdateTaskTypeCommand, UpdateTrackedCountTypeCommand, UpdateTrackedDaysSinceLastIncidentTypeCommand,
    UpdateTrackedEventTypeCommand,
    UploadResponseViewModel,
    WeekViewModel
} from '@/models/api';
import AppHttp from '@/lib/AppHttp';

export class Reporting {
    private resource: string = 'Reporting';

    public async getDailyReportConfig(departmentId: string): Promise<DepartmentDailyReportConfigurationViewModel> {
        const response = await AppHttp.get(`${this.resource}/${departmentId}/dailyReportConfig`);
        const data = response ? response.data : [];
        return data;
    }

    public async updateDailyReportConfig(departmentId: string, config: UpdateDepartmentDailyReportConfigurationCommand): Promise<any> {
        const response = await AppHttp.put(`${this.resource}/${departmentId}/dailyReportConfig`, config);
        const data = response ? response.data : [];
        return data;
    }

    public async getReportingMonthsForMineArea(mineAreaId: string): Promise<ReportingMonthYearViewModel[]> {
        const response = await AppHttp.get(`Calendar/${mineAreaId}/months`);
        const data = response ? response.data : [];
        return data;
    }

    public async generateCalendarReportingMonthsForYear(mineAreaId: string, year: number) {
        const response = await AppHttp.post(`Calendar/${mineAreaId}/months/${year}`, null);
        const data = response ? response.data : [];
        return data;
    }

    public async copyReportingMonthsFromExistingYear(mineAreaId: string, targetYear: number, templateYear: number) {
        const response = await AppHttp.post(`Calendar/${mineAreaId}/months/${targetYear}/fromExisting/${templateYear}`, null);
        const data = response ? response.data : [];
        return data;
    }

    public async uploadReportingMonthsForDepartmentForValidation(mineAreaId: string, file: any): Promise<ReportingMonthsImportViewModel> {
        const response = await AppHttp.postWithFiles(`Calendar/${mineAreaId}/months/uploadImport`, {}, [file]);
        const data = response ? response.data : null;
        return data;
    }

    public async saveReportingMonthsForDepartment(mineAreaId: string, file: any): Promise<UploadResponseViewModel> {
        const response = await AppHttp.postWithFiles(`Calendar/${mineAreaId}/months/saveImport`, {}, [file]);
        const data = response ? response.data || { status: 2 } : { status: 2 };
        return data;
    }

    public async exportReportingMonthsForMineArea(mineAreaId: string): Promise<ExportResponse> {
        const response = await AppHttp.get(`Calendar/${mineAreaId}/months/export`);
        const data = response ? response.data : null;
        return data;
    }

    public async uploadProjectKeyPhysicalTargets(departmentId: string, fromDate: string, file: any): Promise<KeyPhysicalTargetsImportViewModel> {
        const urlParams = new URLSearchParams({ fromDate });

        const response = await AppHttp.postWithFiles(`${this.resource}/${departmentId}/targets/project/uploadImport?${urlParams.toString()}`, {}, [file]);
        const data = response ? response.data : null;
        return data;
    }

    public async saveProjectKeyPhysicalTargets(departmentId: string, fromDate: string, file: any): Promise<UploadResponseViewModel> {
        const urlParams = new URLSearchParams({ fromDate });

        const response = await AppHttp.postWithFiles(`${this.resource}/${departmentId}/targets/project/saveImport?${urlParams.toString()}`, {}, [file]);
        const data = response ? response.data : null;
        return data;
    }

    public async uploadMonthKeyPhysicalTargets(departmentId: string, targetYear: number, targetMonth: number, file: any): Promise<KeyPhysicalTargetsImportViewModel> {
        const urlParams = new URLSearchParams({ targetYear: `${targetYear}`, targetMonth: `${targetMonth}` });

        const response = await AppHttp.postWithFiles(`${this.resource}/${departmentId}/targets/month/uploadImport?${urlParams.toString()}`, {}, [file]);
        const data = response ? response.data : null;
        return data;
    }

    public async saveMonthKeyPhysicalTargets(departmentId: string, targetYear: number, targetMonth: number, file: any): Promise<UploadResponseViewModel> {
        const urlParams = new URLSearchParams({ targetYear: `${targetYear}`, targetMonth: `${targetMonth}` });

        const response = await AppHttp.postWithFiles(`${this.resource}/${departmentId}/targets/month/saveImport?${urlParams.toString()}`, {}, [file]);
        const data = response ? response.data : null;
        return data;
    }

    public async getKeyPhysicalTargetsForWeek(mineAreaId: string, departmentId: string, date:string ): Promise<GetKeyPhysicalTargetsForDepartmentWeekQueryResult> {
        const params = {
            mineAreaId,
            departmentId,
            date
        } as any;
        const response = await AppHttp.get(`${this.resource}/${departmentId}/targets/forWeek?${new URLSearchParams(params).toString()}`);
        const data = response ? response.data : null;
        return data;
    }

    public async getWeekHasTargets(mineAreaId: string, departmentId: string, date: string): Promise<GetWeekHasExistingKeyPhysicalTargetsQueryResponse> {
        const params = {
            mineAreaId,
            departmentId,
            date
        } as any;
        const response = await AppHttp.get(`${this.resource}/${departmentId}/targets/forWeek/exist?${new URLSearchParams(params).toString()}`);
        const data = response ? response.data : null;
        return data;
    }

    public async generateWeekTargets(mineAreaId: string, departmentId: string, date: string, generateDayTargets: boolean = false): Promise<GetWeekHasExistingKeyPhysicalTargetsQueryResponse> {
        const params = {
            mineAreaId,
            departmentId,
            date,
            generateDayTargets
        } as any;
        const response = await AppHttp.post(`${this.resource}/${departmentId}/targets/forWeek/generate?${new URLSearchParams(params).toString()}`, null);
        const data = response ? response.data : null;
        return data;
    }

    public async getPhysicalTargetTypesByDepartment(departmentId: string, include?: string[]): Promise<PhysicalTargetTypeViewModel[]> {
        let query = 'departmentId=' + departmentId;
        if (include) {
            const p: string = include.map((x) => `include=${x}`).join('&');
            query = query + '&' + p;
        }

        query = '?' + query;

        const response = await AppHttp.get(`${this.resource}/${departmentId}/targetTypes${query}`);

        const data = response ? response.data : null;
        return data;
    }

    public async canDeletePhysicalTargetType(departmentId:string, id: string): Promise<CanDeletePhysicalTargetTypeResponse> {
        const response = await AppHttp.get(`${this.resource}/${departmentId}/targetTypes/${id}/CanDelete`);

        const data = response ? response.data : null;
        return data;
    }

    public async updatePhysicalTargetType(departmentId: string, command: UpdatePhysicalTargetTypeCommand) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/targetTypes/Update`, command);
        const data = response ? response.data : null;
        return data;
    }

    public async createPhysicalTargetType(departmentId: string, command: CreatePhysicalTargetTypeCommand) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/targetTypes/Create`, command);
        const data = response ? response.data : null;
        return data;
    }

    public async deletePhysicalTargetType(departmentId:string, id: string) {
        const response = await AppHttp.delete(`${this.resource}/${departmentId}/targetTypes/${id}`);
        const data = response ? response.data : null;
        return data;
    }

    // TrackedEventTypes methods
    public async getTrackedEventTypes(departmentId: string): Promise<TrackedEventTypeViewModel[]> {
        const response = await AppHttp.get(`${this.resource}/${departmentId}/trackedEventTypes`);
        const data = response ? response.data : null;
        return data;
    }

    public async createTrackedEventType(departmentId: string, command: CreateTrackedEventTypeCommand) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedEventTypes/Create`, command);
        const data = response ? response.data : null;
        return data;
    }

    public async updateTrackedEventType(departmentId: string, command: UpdateTrackedEventTypeCommand) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedEventTypes/Update`, command);
        const data = response ? response.data : null;
        return data;
    }

    public async archiveTrackedEventType(departmentId: string, id: string) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedEventTypes/${id}/Archive`, null);
        const data = response ? response.data : null;
        return data;
    }

    public async restoreTrackedEventType(departmentId: string, id: string) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedEventTypes/${id}/Restore`, null);
        const data = response ? response.data : null;
        return data;
    }

    public async removeTrackedEventWeeklyTarget(departmentId: string, id: string) {
        const response = await AppHttp.delete(`${this.resource}/${departmentId}/trackedEventTypes/${id}/WeeklyTarget`);
        const data = response ? response.data : null;
        return data;
    }

    public async removeTrackedEventDailyTarget(departmentId: string, id: string) {
        const response = await AppHttp.delete(`${this.resource}/${departmentId}/trackedEventTypes/${id}/DailyTarget`);
        const data = response ? response.data : null;
        return data;
    }

    public async setTrackedEventWeeklyTarget(departmentId: string, id: string, weeklyTarget: number) {
        const response = await AppHttp.put(`${this.resource}/${departmentId}/trackedEventTypes/${id}/WeeklyTarget/${weeklyTarget}`, null);
        const data = response ? response.data : null;
        return data;
    }

    public async setTrackedEventDailyTarget(departmentId: string, id: string, dailyTarget: number) {
        const response = await AppHttp.put(`${this.resource}/${departmentId}/trackedEventTypes/${id}/DailyTarget/${dailyTarget}`, null);
        const data = response ? response.data : null;
        return data;
    }

    // TrackedCountTypes methods
    public async getTrackedCountTypes(departmentId: string): Promise<TrackedCountTypeViewModel[]> {
        const response = await AppHttp.get(`${this.resource}/${departmentId}/trackedCountTypes`);
        const data = response ? response.data : null;
        return data;
    }

    public async createTrackedCountType(departmentId: string, command: CreateTrackedCountTypeCommand) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedCountTypes/Create`, command);
        const data = response ? response.data : null;
        return data;
    }

    public async updateTrackedCountType(departmentId: string, command: UpdateTrackedCountTypeCommand) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedCountTypes/Update`, command);
        const data = response ? response.data : null;
        return data;
    }

    public async archiveTrackedCountType(departmentId: string, id: string) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedCountTypes/${id}/Archive`, null);
        const data = response ? response.data : null;
        return data;
    }

    public async restoreTrackedCountType(departmentId: string, id: string) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedCountTypes/${id}/Restore`, null);
        const data = response ? response.data : null;
        return data;
    }

    public async setTrackedCountValue(departmentId: string, id: string, value: number) {
        const response = await AppHttp.put(`${this.resource}/${departmentId}/trackedCountTypes/${id}/${value}`, null);
        const data = response ? response.data : null;
        return data;
    }

    // TrackedDaysSinceLastIncident methods
    public async getTrackedDaysSinceIncidentTypes(departmentId: string): Promise<TrackedDaysSinceLastIncidentTypeViewModel[]> {
        const response = await AppHttp.get(`${this.resource}/${departmentId}/trackedDaysSinceIncidentTypes`);
        const data = response ? response.data : null;
        return data;
    }

    public async createTrackedDaysSinceIncidentType(departmentId: string, command: CreateTrackedDaysSinceLastIncidentTypeCommand) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedDaysSinceIncidentTypes/Create`, command);
        const data = response ? response.data : null;
        return data;
    }

    public async updateTrackedDaysSinceIncidentType(departmentId: string, command: UpdateTrackedDaysSinceLastIncidentTypeCommand) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedDaysSinceIncidentTypes/Update`, command);
        const data = response ? response.data : null;
        return data;
    }

    public async archiveTrackedDaysSinceIncidentType(departmentId: string, id: string) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedDaysSinceIncidentTypes/${id}/Archive`, null);
        const data = response ? response.data : null;
        return data;
    }

    public async restoreTrackedDaysSinceIncidentType(departmentId: string, id: string) {
        const response = await AppHttp.post(`${this.resource}/${departmentId}/trackedDaysSinceIncidentTypes/${id}/Restore`, null);
        const data = response ? response.data : null;
        return data;
    }

    public async setTrackedDaysSinceLastIncident(departmentId: string, id: string, date: string) {
        const response = await AppHttp.put(`${this.resource}/${departmentId}/trackedDaysSinceIncidentTypes/${id}/${date}`, null);
        const data = response ? response.data : null;
        return data;
    }
}

export default new Reporting();
